import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import SLACountdown from "../shared/SLACountdown";
import DecisionForm from "../../../components/dr/DecisionForm";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Btn from "../../../components/v3/Btn";
import ApprovalContextCards, { investorName, personName, KeyVal } from "./ApprovalContextCards";
import OversightActions from "./OversightActions";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import { apiErrorCode, apiErrorMessage } from "../../../utils/apiError";

const STATUS_CLASS = {
  PENDING:   "s-rev",
  COMPLETED: "s-active",
  BREACHED:  "s-fail",
  CANCELLED: "s-mute",
};

const AD_CANNOT_DECIDE =
  "Platform Manager sessions can reassign or escalate but do not decide KYC, classification or suitability.";
const CREDENTIAL_BLOCKED =
  "Your reviewer credential is missing or expired. Renew it on the Credentials page before deciding.";
const ALREADY_DECIDED = "Another reviewer has already decided this record.";

function DRApprovalDetail() {
  const { slaId } = useParams();
  const navigate = useNavigate();
  // Deciding is a role, not a rank: DR, CCO and UDP all decide, and a Platform
  // Manager holding none of them oversees instead. The old branch keyed on AD,
  // which put a CCO who also holds AD on the wrong side of every gate here.
  // Two independent questions. canDecide chooses the live form; isAD decides
  // whether the oversight actions exist at all. They are not opposites: an
  // AD who also holds CCO both decides AND hands work on, and hanging
  // OversightActions off the else-branch of canDecide hid reassignment from
  // exactly the people most likely to need it.
  const { canDecide, isAD } = useRole();
  const queueRoute = canDecide ? "/dr/approvals" : "/ad/dr-oversight/sla-breaches";
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [investorMessage, setInvestorMessage] = useState("");

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaId]);

  async function fetchDetail() {
    setLoading(true);
    try {
      const res = await drApi.approvalDetail(slaId);
      setPayload(res?.data || null);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, "Fetch failed"));
    } finally {
      setLoading(false);
    }
  }

  async function submitDecision({ decision, notes }) {
    try {
      const body = { decision, notes };
      const message = investorMessage.trim();
      if (message) body.investor_message = message;
      const res = await drApi.decide(slaId, body);
      // Honest copy: the investor notification is enqueued (cron-delivered),
      // not sent synchronously. Delivery is confirmable on the email log.
      setFlash({
        tone: "info",
        text: `Decision recorded, ${res?.data?.decision || decision}. Investor notification queued. Returning to queue...`,
      });
      setInvestorMessage("");
      setTimeout(() => navigate(queueRoute), 1200);
    } catch (err) {
      const status = err?.response?.status;
      const code = apiErrorCode(err);
      if (status === 409) {
        // The losing half of a race, not a failure: say so and refetch so the
        // decision that won is on screen.
        setFlash({ tone: "danger", text: ALREADY_DECIDED });
        await fetchDetail();
        return;
      }
      // Rethrown rather than flashed: DecisionForm renders a submit failure
      // inline, next to the control that produced it.
      if (status === 403 && code === "ad_cannot_decide") {
        throw new Error(AD_CANNOT_DECIDE);
      }
      if (status === 403 && code === "dr_credential_expired_or_missing") {
        throw new Error(CREDENTIAL_BLOCKED);
      }
      throw err;
    }
  }

  // The oversight actions unmount themselves by refetching, so the page owns
  // the confirmation they produce.
  async function handleOversightDone(message) {
    if (message) setFlash({ tone: "info", text: message });
    await fetchDetail();
  }

  const sla = payload?.sla;
  const investor = payload?.investor;
  // The detail payload has no `decided_by_user_id` field at all: it publishes
  // the DecidedBy association and the decision instead. Keying only on the id
  // meant a decided item still rendered a live decision form.
  const isDecided =
    sla?.status === "COMPLETED" ||
    !!sla?.decided_by_user_id ||
    !!sla?.DecidedBy ||
    !!sla?.decided_at;

  const decidedByName = personName(
    sla?.DecidedBy || sla?.decided_by,
    sla?.decided_by_user_id
  );

  const headActions = (
    <Btn variant="ghost" size="sm" onClick={() => navigate(queueRoute)}>
      <ChevronLeft /> Back to {canDecide ? "queue" : "SLA breaches"}
    </Btn>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title={sla ? `${sla.submission_type} · SLA #${sla.id}` : "Approval detail"}
        subtitle={sla && investor ? <span>{investorName(investor)}</span> : null}
        actions={headActions}
      />

      {canDecide && <CredentialExpiryAlert />}

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      {!loading && sla && (
        <>
          <section className="card">
            <div className="card__head">
              <div>
                <div className="card__title">{investorName(investor)}</div>
                <div className="card__sub mono">
                  Submitted {new Date(sla.submitted_at).toLocaleString()}
                </div>
              </div>
              <div className="card__head-right">
                <SLACountdown deadline={sla.deadline_at} size="large" />
                <span className={`tbl-status ${STATUS_CLASS[String(sla.status).toUpperCase()] || "s-mute"}`}>
                  {sla.status}{sla.decision ? ` · ${sla.decision}` : ""}
                </span>
              </div>
            </div>
          </section>

          <ApprovalContextCards payload={payload} />

          <section className="card">
            <div className="card__head"><div className="card__title">Decision</div></div>
            <div className="card__body">
              {isDecided ? (
                <>
                  <KeyVal label="Decision" value={sla.decision || "-"} />
                  <KeyVal label="Decided by" value={decidedByName} />
                  <KeyVal
                    label="Decided at"
                    value={sla.decided_at ? new Date(sla.decided_at).toLocaleString() : "-"}
                  />
                  <KeyVal label="Review status" value={sla.review_status || "Not reviewed"} />
                </>
              ) : canDecide ? (
                <>
                  <DecisionForm
                    notesLabel="Decision notes (regulatory audit trail)"
                    submitLabel="Record decision"
                    onSubmit={submitDecision}
                  />
                  <div className="field" style={{ marginTop: 12 }}>
                    <label className="field__label" htmlFor="dr-investor-message">
                      Message to the investor (optional)
                    </label>
                    <textarea
                      id="dr-investor-message"
                      className="field__textarea"
                      rows={3}
                      value={investorMessage}
                      onChange={(e) => setInvestorMessage(e.target.value)}
                      placeholder="Included in the notification sent to the investor."
                    />
                  </div>
                </>
              ) : (
                <DecisionForm disabled onSubmit={submitDecision} />
              )}
            </div>
          </section>

          {isAD && !isDecided && (
            <section className="card">
              <div className="card__head">
                <div className="card__title">Platform Manager actions</div>
              </div>
              <div className="card__body">
                <OversightActions slaId={slaId} onDone={handleOversightDone} />
              </div>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}

export default DRApprovalDetail;
